"use client";

import { useLocale, useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import { Mail, LogIn } from "lucide-react";
import { motion } from "framer-motion";
import Link from "next/link";
import { useResetPassword } from "@/hooks/main-hook";
import Loading from "./Loading";
import { useRouter } from "next/navigation";

export default function ResetPassword() {
  const navigate = useRouter();
  const locale = useLocale();
  const t = useTranslations("Auth");
  const { mutate, isPending } = useResetPassword();
  const isRtl = locale === "ar";
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: "all" });

  // const PRIMARY_COLOR_HEX = "#d99641";

  const handleLogin = (data: any) => {
    mutate(data, {
      onSuccess: () => {
        navigate.push(`/auth/login`);
      },
    });
  };

  return (
    <section
      dir={isRtl ? "rtl" : "ltr"}
      className="relative min-h-screen flex items-center justify-center bg-gray-950 text-white overflow-hidden"
    >
      <div className="absolute inset-0 opacity-5 bg-[url('/path/to/architectural-pattern.svg')] bg-repeat" />

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary/20 rounded-full blur-[150px] opacity-20" />

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative z-10 w-full max-w-xl p-8 md:p-12 bg-gray-900 border border-primary/30 rounded-2xl shadow-2xl shadow-primary/20 backdrop-blur-sm"
      >
        {/* Card Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-extrabold text-white mb-2">
            {t("welcome")}
          </h1>
          <p className="text-gray-400 text-lg">{t("reset_sub")}</p>
        </div>

        {/* 3. Form */}
        <form onSubmit={handleSubmit(handleLogin)} className="space-y-6">
          {/* Email Input */}
          <div className="relative">
            <Mail
              className={`absolute top-1/2 ${
                isRtl ? "right-4" : "left-4"
              } transform -translate-y-1/2 w-5 h-5 text-primary`}
            />
            <input
              type="email"
              placeholder={t("email")}
              className={`w-full py-3 ${
                isRtl ? "pr-12 pl-4" : "pl-12 pr-4"
              } bg-gray-800 border ${
                errors.email ? "border-red-500" : "border-gray-700"
              } rounded-lg text-white placeholder-gray-500 focus:ring-1 focus:ring-primary focus:border-primary transition-all duration-300`}
              {...register("email", {
                required: t("required"),
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: t("invalid_email"),
                },
              })}
            />
          </div>

          {isPending ? (
            <Loading />
          ) : (
            <motion.button
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full flex items-center justify-center py-3 bg-primary text-gray-900 font-bold text-lg rounded-lg shadow-lg shadow-primary/50 transition-all duration-300 hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-gray-900 cursor-pointer"
            >
              <LogIn className={`w-5 h-5 ${isRtl ? "ml-2" : "mr-2"}`} />
              {t("reset")}
            </motion.button>
          )}
        </form>

        {/* 4. Registration Link */}
        {!isPending && (
          <p className="mt-8 text-center text-gray-400">
            {t("have")}{" "}
            <Link
              href="/auth/login"
              className="font-semibold text-primary hover:text-primary/80 transition-colors duration-300"
            >
              {t("login")}
            </Link>
          </p>
        )}
      </motion.div>
    </section>
  );
}
